import Editor from 'components/Editor/Editor'
import Loader from 'components/shared/Loader/Loader'
import SiteHeader from 'components/shared/SiteHeader/SiteHeader'
import { createAnswerHandler } from 'helpers/KnowledgeQuiz'
import { initAnswerHandler, initEditorQuiz } from 'helpers/Question'
import { getElementOnShadowRoot } from 'helpers/ShadowRoot'
import { isKnowledgeQuiz } from 'helpers/Step'
import { signUpQueryParams, userManager } from 'helpers/authentication'
import { withRouter } from 'helpers/withRouter'
import { useProgress } from 'hooks/useProgress'
import { find, some } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { withCookies } from 'react-cookie'
import { Helmet } from 'react-helmet'
import { translate } from 'react-i18next'
import { connect } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { fetchProject, setProjectStep } from 'redux/actions/ProjectActions'
import { fetchQuiz } from 'redux/actions/QuizActions'
import { initQuizAttempt } from 'redux/actions/QuizAttemptActions'
import {
  addAnswerToCurrentAttempt,
  sendQuizAnswer,
  setCurrentQuestion,
} from 'redux/actions/QuizAttemptActions'

const ProjectEditorContainer = ({
  authentication,
  dispatch,
  cookies,
  currentProjectSlug,
  i18n,
  project,
  projectFetched,
  projectLoading,
  projectProgress,
  currentStepPosition,
  quiz,
  quizAttempt,
  t,
  router: { params, navigate },
}) => {
  const { slug } = params
  const searchParams = useSearchParams()[0]
  const branch = searchParams.get('branch')
  const session = cookies.get('sessionProgress')
  const elementRef = useRef()
  const [answerInit, setAnswerInit] = useState(false)
  const { answers = [], currentQuestion = -1, ...currentAttempt } = quizAttempt
  const showDebugRoutes = import.meta.env.VITE_SHOW_DEBUG_ROUTES

  const addQuizAnswerAction = (newAnswer, attempt) =>
    dispatch(addAnswerToCurrentAttempt(newAnswer, attempt))
  const sendQuizAnswerAction = (quizId, newAnswer, attempt) =>
    dispatch(sendQuizAnswer(quizId, newAnswer, attempt))
  const setCurrentQuestionAction = (questionNumber) =>
    dispatch(setCurrentQuestion(questionNumber))

  const getElementRef = () => {
    elementRef.current = getElementOnShadowRoot(
      'editor-wc',
      '.project-instructions'
    )
  }

  const getOrderAndAnswerStatus = useCallback(
    (answer) => {
      const currentAnswers = answers[currentQuestion + 1] || []
      const answered = some(currentAnswers, ['result', 'correct'])
      if (find(currentAnswers, { answer })) {
        return [-1, answered]
      }

      return [currentAnswers.length, answered]
    },
    [answers, currentQuestion]
  )

  const checkLast = useCallback(() => {
    return currentQuestion + 1 === quiz.questionCount
  }, [currentQuestion, quiz.questionCount])

  const answerQuestion = createAnswerHandler(
    quiz.id,
    currentAttempt,
    currentQuestion,
    addQuizAnswerAction,
    sendQuizAnswerAction
  )

  const nextQuestion = useCallback(() => {
    setCurrentQuestionAction(currentQuestion + 1)
  }, [currentQuestion, setCurrentQuestionAction])

  const fetchKnowledgeQuiz = (step) => {
    let quizPath = step.knowledgeQuiz
    dispatch(
      fetchQuiz({
        currentLocale: i18n.language,
        currentProject: slug,
        quizPath,
        branch,
      })
    )
  }

  const projectHasSteps = () => {
    return !!currentStepPosition && !!project.steps
  }

  const setAuthReturnUrl = () => {
    localStorage.setItem('authReturnUrl', window.location.href)
  }

  const redirectToLogIn = (e) => {
    if (!authentication?.userId) {
      e.preventDefault()
      setAuthReturnUrl()
      userManager.signinRedirect()
    }
  }

  const redirectToSignUp = (e) => {
    e.preventDefault()
    setAuthReturnUrl()
    userManager.signinRedirect({ extraQueryParams: signUpQueryParams })
  }

  // Redirect to homepage if project loaded and there's no associated starter project
  useEffect(() => {
    if (
      showDebugRoutes !== 'true' &&
      !projectLoading &&
      !project.editorStarterProject
    ) {
      navigate('/')
    }
  }, [projectLoading, project.editorStarterProject])

  // Fetch project content if slug/locale changes
  useEffect(() => {
    if (!!project.projectFetched && !project.slug) {
      return // Project not available
    }
    dispatch(fetchProject({ locale: i18n.language, position: 0, slug, branch }))
  }, [i18n.language, slug, branch])

  useEffect(() => {
    if (!projectHasSteps()) {
      return
    }
    const step = project?.steps[currentStepPosition]
    if (isKnowledgeQuiz(step)) {
      fetchKnowledgeQuiz(step)
    }
  }, [currentStepPosition, project.steps])

  useEffect(() => {
    if (!projectLoading && quiz.id && session) {
      dispatch(
        initQuizAttempt({
          currentLocale: quiz.currentLocale,
          quizLength: quiz.questionCount,
          quizVersion: quiz.version,
          userId: authentication?.userId,
          sessionId: session,
        })
      )
    }
  }, [quiz])

  useEffect(() => {
    const resultHandler = (data) => {
      answerQuestion(data)
    }

    if (answerInit) {
      initAnswerHandler(
        elementRef.current,
        getOrderAndAnswerStatus,
        checkLast,
        resultHandler,
        nextQuestion,
        t
      )
      setAnswerInit(false)
    }
  }, [
    answerQuestion,
    getOrderAndAnswerStatus,
    checkLast,
    nextQuestion,
    answers,
    t,
    answerInit,
  ])

  const webComponent = `${import.meta.env.VITE_EDITOR_WEB_COMPONENT_URL}/web-component.js`
  const handleEditorStepChange = (e) => {
    dispatch(setProjectStep(e.detail))
  }

  useEffect(() => {
    document.addEventListener('editor-stepChanged', handleEditorStepChange)
    document.addEventListener('editor-logIn', redirectToLogIn)
    document.addEventListener('editor-signUp', redirectToSignUp)
    document.addEventListener('editor-quizReady', () => {
      getElementRef()
      initEditorQuiz(elementRef, t, setAnswerInit)
    })
  }, [])

  useProgress({
    currentProjectSlug,
    currentStepPosition,
    dispatch,
    i18n,
    session,
    authentication,
    slug,
    project,
    projectFetched,
    projectProgress,
  })

  return (
    <>
      <SiteHeader locale={i18n.language} />
      <Helmet>
        <link
          rel="canonical"
          href={`https://projects.raspberrypi.org/${i18n.language}/projects/${slug}`}
        />
        <script
          defer
          data-domain={import.meta.env.VITE_PLAUSIBLE_DATA_DOMAIN}
          src={import.meta.env.VITE_PLAUSIBLE_SOURCE}
        ></script>
        <script>
          {`
            (window.plausible =
              window.plausible ||
              function (...args) {
                ;(window.plausible.q = window.plausible.q || []).push(...args)
              })`}
        </script>
        <script src={webComponent}></script>
      </Helmet>
      {projectLoading ? (
        <Loader />
      ) : (
        <Editor
          locale={i18n.language}
          project={project}
          currentStepPosition={currentStepPosition}
          quiz={{ ...quiz, currentQuestion: quizAttempt?.currentQuestion }}
        />
      )}
    </>
  )
}

ProjectEditorContainer.propTypes = {
  cookies: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  currentProjectSlug: PropTypes.string,
  router: PropTypes.shape({
    params: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    language: PropTypes.string.isRequired,
  }).isRequired,
  project: PropTypes.object,
  projectFetched: PropTypes.number.isRequired,
  projectLoading: PropTypes.bool,
  projectProgress: PropTypes.object,
  currentStepPosition: PropTypes.number.isRequired,
  authentication: PropTypes.object,
  quiz: PropTypes.object,
  quizAttempt: PropTypes.object,
  t: PropTypes.func.isRequired,
  quizId: PropTypes.number,
}

const mapStateToProps = (state) => {
  const {
    project,
    projectFetched,
    loading: projectLoading,
    slug,
    currentStepPosition,
  } = state.project
  const { authentication, quiz, quizAttempt } = state
  return {
    currentProjectSlug: slug,
    projectLoading,
    project,
    projectFetched,
    projectProgress: state.projectProgress,
    currentStepPosition,
    authentication,
    quiz,
    quizAttempt,
  }
}

export { ProjectEditorContainer }
export default connect(mapStateToProps)(
  translate('translations')(withCookies(withRouter(ProjectEditorContainer)))
)
